import { useEffect, useState, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from "app/hooks";
import JobSeekerContextProvider from 'components/JobSeekerContextProvider/JobSeekerContextProvider';
import JobSeekerGroupContextProvider from 'components/JobSeekerContextProvider/ProgramContextProvider/JobSeekerProgramContextProvider';
import ScrollToTop from 'components/ScrollToTop';
import { JobSeekerAppNavbar } from 'layout/AppNavbar';
import { JobSeekerHeader } from 'layout/common/Header';
import { selectJobSeekerOnBehalfOfOrganizationAdminId, clearOnBehalfOfOrganizationAdmin } from "features/job-seeker-on-behalf-of-organization-admin";
import { selectJobSeekerOnBehalfOfOrganizationId } from "features/job-seeker-on-behalf-of-organization";
import { selectAdminInCommunitiesSelectList } from 'features/job-seeker-auth';
import JobSeekerMobileFooterNav from 'components/MobileFooterNav/JobSeekerMobileFooterNav';

interface Props {
  children: React.ReactNode;
};

const JobSeekerAppLayout = ({ children }: Props) => {
  const dispatch = useAppDispatch();
  const [layoutStyle, setLayoutStyle] = useState<string>(window.innerWidth > 767 ? 'desktop' : 'mobile');
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const toggleSidebar = () => setSidebarOpen((sidebarOpen) => !sidebarOpen);
  const onBehalfOfOrganizationAdminId = useAppSelector(selectJobSeekerOnBehalfOfOrganizationAdminId);
  const onBehalfOfOrganizationId = useAppSelector(selectJobSeekerOnBehalfOfOrganizationId);
  const isAdminInCommunitiesSelectList = useAppSelector(selectAdminInCommunitiesSelectList);

  const handleResize = () => {
    if (window.innerWidth > 767) {
      setLayoutStyle('desktop');
    } else {
      setLayoutStyle('mobile');
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    }
  }, []);

  useEffect(() => {
    if (onBehalfOfOrganizationAdminId) {
      const exists = isAdminInCommunitiesSelectList.some(item => item.value === onBehalfOfOrganizationAdminId);
      if (!exists) dispatch(clearOnBehalfOfOrganizationAdmin());
    }
  }, [dispatch, isAdminInCommunitiesSelectList, onBehalfOfOrganizationAdminId]);

  const contextKey = useMemo(() => {
    const contextKeys: string[] = [];
    if (onBehalfOfOrganizationAdminId) contextKeys.push(onBehalfOfOrganizationAdminId);
    if (onBehalfOfOrganizationId) contextKeys.push(onBehalfOfOrganizationId);
    if (contextKeys.length === 0) contextKeys.push('member');
    return contextKeys.join('-');
  }, [onBehalfOfOrganizationAdminId, onBehalfOfOrganizationId]);

  return (
    <JobSeekerContextProvider key={`job-seeker-context-${contextKey}`}>
      <ScrollToTop />
      <JobSeekerHeader toggleSidebar={toggleSidebar} />
      <div className={`app app-layout-full app-layout-authorized ${layoutStyle}`.trim()}>
        <JobSeekerAppNavbar sidebarOpen={sidebarOpen} closeSidebar={() => setSidebarOpen(false)} />
        <JobSeekerGroupContextProvider>
          {children}
        </JobSeekerGroupContextProvider>
      </div>
      <JobSeekerMobileFooterNav sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
    </JobSeekerContextProvider>
  );
};

export default JobSeekerAppLayout;
